// Utility functions for handling app releases and downloads

// Get the current app version from package.json
export const getCurrentVersion = () => {
  try {
    // Read package.json from the public directory (it's copied there during build)
    const packageJson = require('../../package.json');
    if (!packageJson.version) {
      throw new Error('Version not found in package.json');
    }
    return packageJson.version;
  } catch (error) {
    console.error('Failed to read version from package.json:', error);
    throw new Error('Unable to determine app version from package.json');
  }
};

// Detect system architecture
export const detectSystemArchitecture = () => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return 'arm64'; // Default fallback for server-side
  }
  
  // Check for Apple Silicon indicators
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  // More reliable detection for Apple Silicon Macs
  // Apple Silicon Macs report as "MacIntel" but have specific characteristics
  
  // Check for explicit Intel indicators first
  if (platform === 'MacIntel' && userAgent.includes('Intel')) {
    return 'x64'; // Intel Mac
  }
  
  // Check for Apple Silicon indicators
  if (platform === 'MacIntel' && !userAgent.includes('Intel')) {
    // This is likely Apple Silicon (M1, M2, M3, etc.)
    // Apple Silicon Macs report as MacIntel but don't have "Intel" in userAgent
    return 'arm64';
  }
  
  // Additional checks for Apple Silicon
  if (platform.includes('Mac')) {
    try {
      // Check for Apple Silicon specific characteristics
      // Apple Silicon Macs typically have more cores and different memory characteristics
      if (navigator.hardwareConcurrency && navigator.hardwareConcurrency >= 8) {
        // Most Apple Silicon Macs have 8+ cores
        return 'arm64';
      }
      
      // Check for Apple Silicon specific APIs or features
      if (navigator.userAgentData && navigator.userAgentData.platform === 'macOS') {
        // Additional check for Apple Silicon
        return 'arm64';
      }
    } catch (e) {
      // If we can't determine, default to Apple Silicon for modern Macs
      return 'arm64';
    }
    
    // Default to Apple Silicon for Mac platforms
    return 'arm64';
  }
  
  return 'arm64'; // Default fallback
};

// Get architecture display name
export const getArchitectureDisplayName = (architecture = null) => {
  const arch = architecture || detectSystemArchitecture();
  return arch === 'x64' ? 'Intel' : 'Apple Silicon';
};

// Get the releases.json URL based on environment
const getReleasesJsonUrl = () => {
  return 'https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/releases.json';
};

// Fetch the latest release information from releases.json
export const fetchLatestRelease = async (architecture = null) => {
  try {
    // Add cache-busting query to avoid stale CDN/browser caches
    const baseUrl = getReleasesJsonUrl();
    const url = new URL(baseUrl);
    url.searchParams.set('_ts', String(Date.now()));
    const response = await fetch(url.toString(), { cache: 'no-store' });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch releases: ${response.statusText}`);
    }
    
    const releases = await response.json();
    
    if (!Array.isArray(releases) || releases.length === 0) {
      throw new Error('No releases found');
    }
    
    // Return the first (latest) release with all architecture URLs
    const latestRelease = releases[0];
    
    return {
      ...latestRelease,
      // Keep all architecture-specific URLs available
      dmgUrlIntel: latestRelease.dmgUrlIntel,
      zipUrlIntel: latestRelease.zipUrlIntel,
      dmgUrlArm: latestRelease.dmgUrlArm,
      zipUrlArm: latestRelease.zipUrlArm,
      // Default URLs (for backward compatibility)
      dmgUrl: latestRelease.dmgUrl,
      zipUrl: latestRelease.zipUrl
    };
  } catch (error) {
    console.error('Error fetching latest release:', error);
    
    // Fallback to a default release structure with both architectures
    const currentVersion = getCurrentVersion();
    
    return {
      version: currentVersion,
      dmgUrl: `https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/Motion Air Calendar-${currentVersion}-arm64.dmg`,
      zipUrl: `https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/Motion Air Calendar-${currentVersion}-arm64-mac.zip`,
      dmgUrlIntel: `https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/Motion Air Calendar-${currentVersion}.dmg`,
      zipUrlIntel: `https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/Motion Air Calendar-${currentVersion}-mac.zip`,
      dmgUrlArm: `https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/Motion Air Calendar-${currentVersion}-arm64.dmg`,
      zipUrlArm: `https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/Motion Air Calendar-${currentVersion}-arm64-mac.zip`,
      note: 'Latest release'
    };
  }
};

// Get the download URL for the Mac app (DMG)
export const getMacAppDownloadUrl = async (architecture = null) => {
  try {
    const latestRelease = await fetchLatestRelease(architecture);
    
    // Return architecture-specific URL if requested
    if (architecture === 'x64') {
      return latestRelease.dmgUrlIntel || latestRelease.dmgUrl;
    } else if (architecture === 'arm64') {
      return latestRelease.dmgUrlArm || latestRelease.dmgUrl;
    }
    
    // Default to ARM64
    return latestRelease.dmgUrlArm || latestRelease.dmgUrl;
  } catch (error) {
    console.error('Error getting Mac app download URL:', error);
    
    // Fallback URL using current version
    const currentVersion = getCurrentVersion();
    
    if (architecture === 'x64') {
      return `https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/Motion Air Calendar-${currentVersion}.dmg`;
    } else {
      return `https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/Motion Air Calendar-${currentVersion}-arm64.dmg`;
    }
  }
};

// Get the download URL for the Mac app ZIP
export const getMacAppZipUrl = async (architecture = null) => {
  try {
    const latestRelease = await fetchLatestRelease(architecture);
    
    // Return architecture-specific URL if requested
    if (architecture === 'x64') {
      return latestRelease.zipUrlIntel || latestRelease.zipUrl;
    } else if (architecture === 'arm64') {
      return latestRelease.zipUrlArm || latestRelease.zipUrl;
    }
    
    // Default to ARM64
    return latestRelease.zipUrlArm || latestRelease.zipUrl;
  } catch (error) {
    console.error('Error getting Mac app ZIP URL:', error);
    
    // Fallback URL using current version
    const currentVersion = getCurrentVersion();
    
    if (architecture === 'x64') {
      return `https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/Motion Air Calendar-${currentVersion}-mac.zip`;
    } else {
      return `https://pub-3249a50a494f41a39a6dc9a6e607a0f9.r2.dev/Motion Air Calendar-${currentVersion}-arm64-mac.zip`;
    }
  }
};
